/**
 * ----------------------------------------------------------------------------------------------------------------------
 * VRAC FORMAT SYNC
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Répercute le format vrac choisi (300 g, 500 g...) sur la quantité du formulaire et sur tous les prix affichés.
 *
 * Les formats ne sont PAS des variantes Shopify : ils sélectionnent la variante vrac de base et posent une quantité.
 * Les montants sont pré-formatés en Liquid et transportés dans le <script data-vrac-formats> du composant, ce qui évite
 * de reformater des devises en JS.
 *
 * Rendu par `snippets/vrac-format-picker.liquid`, qui émet aussi la balise <script> chargeant ce fichier.
 * Chargé en `defer` : à l'exécution le document est déjà parsé, donc le <form> de buy-buttons existe (voir la note
 * dans connectedCallback).
 */

if (!customElements.get('vrac-format-sync')) {
  class VracFormatSync extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute('form-id');
      this.variantId = String(this.getAttribute('variant-id'));
      this.optionValueId = this.getAttribute('option-value-id');
      this.defaultQuantity = parseInt(this.getAttribute('default-quantity'), 10) || 1;
      this.shouldUpdateUrl = this.hasAttribute('update-url');

      try {
        this.formats = JSON.parse(this.querySelector('script[data-vrac-formats]').textContent);
      } catch (error) {
        this.formats = [];
      }

      if (!this.formats.length) return;

      this.picker = this.closest('variant-picker');

      if (!this.picker) return;

      // Le <form> est émis PLUS BAS dans le document que ce composant : il vit dans `buy-buttons`, alors que
      // le sélecteur de format est rendu dans <variant-picker>. Si ce fichier venait à être chargé pendant le
      // parsing, `getElementById` renverrait null et la résolution échouerait en silence — aucun écouteur ne
      // serait attaché et le composant resterait inerte pour toute la durée de vie de la page.
      // Le chargement en `defer` place déjà l'exécution après le parsing ; ce garde reste une sécurité si le
      // fichier était un jour chargé autrement. Au retour d'un rerender partiel, le document est de toute façon
      // déjà parsé et l'initialisation est immédiate.
      if (document.readyState === 'loading') {
        this.onDomReady = () => this.init();
        document.addEventListener('DOMContentLoaded', this.onDomReady, { once: true });
      } else {
        this.init();
      }
    }

    init() {
      this.form = document.getElementById(this.formId);

      if (!this.form) return;

      this.scope = this.closest('.product-info') || document;
      this.pendingQuantity = null;
      this.currentQuantity = null;

      // Liaison une seule fois : `init` peut être rejoué à une reconnexion du composant, et
      // `disconnectedCallback` doit pouvoir retirer exactement les fonctions qui ont été attachées.
      if (!this.handlersBound) {
        this.onChange = this.onChange.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.handlersBound = true;
      }

      this.picker.removeEventListener('change', this.onChange);
      this.form.removeEventListener('variant:change', this.onVariantChange);

      this.picker.addEventListener('change', this.onChange);
      this.form.addEventListener('variant:change', this.onVariantChange);

      // Deep link ?vrac_format=3 — Liquid ne peut pas lire les query params, la présélection est donc
      // forcément JS. Le paramètre NE DOIT PAS s'appeler `format` : ce nom est réservé côté plateforme et
      // fait répondre 404 au storefront, quelle que soit sa valeur (vérifié en preview le 03/08/2026 —
      // `?format=5` et `?format=abc` renvoient 404, `?fmt`, `?qty`, `?formats`, `?Format` renvoient 200).
      const requested = parseInt(new URLSearchParams(window.location.search).get('vrac_format'), 10);

      if (requested && this.formats.some((format) => format.qty === requested)) {
        if (this.isVracSelected()) {
          this.applyFormat(requested);
        } else {
          this.pendingQuantity = requested;
          this.selectVracVariant();
        }
      }
    }

    disconnectedCallback() {
      if (this.onDomReady) document.removeEventListener('DOMContentLoaded', this.onDomReady);
      if (this.picker) this.picker.removeEventListener('change', this.onChange);
      if (this.form) this.form.removeEventListener('variant:change', this.onVariantChange);
    }

    isVracSelected() {
      const input = this.form.querySelector('input[name="id"]');
      return input ? String(input.value) === this.variantId : false;
    }

    selectVracVariant() {
      const native = this.picker.querySelector(
        'input[data-option-position][value="' + this.optionValueId + '"]'
      );
      if (!native) return;

      native.checked = true;
      native.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onChange(event) {
      const input = event.target;
      if (!input || !input.matches || !input.matches('input[data-vrac-format]')) return;

      const quantity = parseInt(input.dataset.vracFormat, 10);
      if (!quantity) return;

      if (this.isVracSelected()) {
        this.applyFormat(quantity);
      } else {
        // La variante vrac n'est pas sélectionnée : on déclenche l'aller-retour serveur du VariantPicker,
        // et le format sera appliqué sur 'variant:change' (émis APRÈS 'product:rerender', donc après que les
        // blocs prix et buy-buttons ont été réécrits).
        this.pendingQuantity = quantity;
        this.selectVracVariant();
      }
    }

    onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      const isVrac = variant && String(variant.id) === this.variantId;

      if (isVrac) {
        const quantity = this.pendingQuantity || this.currentQuantity || this.formats[0].qty;
        this.pendingQuantity = null;
        this.applyFormat(quantity);
        return;
      }

      // On quitte le vrac. Le rerender de buy-buttons PRÉSERVE la quantité précédente (theme.js) : sans ce
      // reset, passer de "500 g" à "Boîte" ajouterait 5 boîtes au panier.
      this.pendingQuantity = null;
      this.currentQuantity = null;
      this.setQuantity(this.defaultQuantity);
      this.checkChip(null);
      this.syncUrl(null);
    }

    setQuantity(quantity) {
      const selector = this.form.querySelector('quantity-selector');

      if (selector) {
        selector.quantity = quantity;
        return;
      }

      const input = this.form.querySelector('input[name="quantity"]');
      if (input) {
        input.value = quantity;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    applyFormat(quantity) {
      const format = this.formats.find((item) => item.qty === quantity);
      if (!format) return;

      this.currentQuantity = quantity;
      this.setQuantity(quantity);
      this.checkChip(quantity);
      this.updateSelectedLabel(format);
      this.repaint(format);
      this.syncUrl(quantity > 1 ? quantity : null);
    }

    checkChip(quantity) {
      this.picker.querySelectorAll('input[data-vrac-format]').forEach((input) => {
        input.checked = parseInt(input.dataset.vracFormat, 10) === quantity;
      });
    }

    // Intitulé « Choisissez votre format : … » du fieldset : le thème n'y écrit que `option.selected_value`, soit
    // toujours le libellé de la variante de base (100 g), quel que soit le format choisi.
    updateSelectedLabel(format) {
      if (!format.label) return;

      const chip = this.picker.querySelector('input[data-vrac-format]');
      const fieldset = chip ? chip.closest('fieldset') : null;
      if (!fieldset) return;

      fieldset.querySelectorAll('.variant-picker__option-info-value, [id$="-selected-value"]').forEach((element) => {
        element.textContent = format.label;
      });
    }

    repaint(format) {
      const priceLists = [
        this.scope.querySelector('[data-block-type="price"] price-list'),
        document.querySelector('product-sticky-add-to-cart price-list')
      ].filter(Boolean);

      priceLists.forEach((list) => {
        const salePrice = list.querySelector('sale-price');

        if (salePrice) {
          const label = salePrice.querySelector('.sr-only');
          salePrice.innerHTML = (label ? label.outerHTML : '') + format.price;
          salePrice.classList.toggle('text-on-sale', format.showCompare);
        }

        let compareAtPrice = list.querySelector('compare-at-price');

        if (format.showCompare) {
          if (!compareAtPrice && salePrice) {
            compareAtPrice = document.createElement('compare-at-price');
            compareAtPrice.className = 'h6 line-through';
            salePrice.after(compareAtPrice);
          }

          if (compareAtPrice) {
            const label = compareAtPrice.querySelector('.sr-only');
            compareAtPrice.innerHTML = (label ? label.outerHTML : '') + format.compareAt;
            compareAtPrice.hidden = false;
          }
        } else if (compareAtPrice) {
          compareAtPrice.hidden = true;
        }

        const pricePer100g = list.querySelector('price-per-100g');
        if (pricePer100g && format.per100g) {
          pricePer100g.textContent = format.per100g;
        }
      });

      const buttonPrices = [
        this.scope.querySelector('.button__price'),
        document.querySelector('product-sticky-add-to-cart .button__price')
      ].filter(Boolean);

      buttonPrices.forEach((element) => {
        element.textContent = format.price;
      });

      const galleryBadge = document.querySelector('[data-vrac-gallery-badge]');
      if (galleryBadge) {
        galleryBadge.innerHTML = format.galleryBadge || '';
        galleryBadge.hidden = !format.galleryBadge;
      }
    }

    syncUrl(quantity) {
      if (!this.shouldUpdateUrl) return;

      const url = new URL(window.location.href);

      if (quantity) {
        url.searchParams.set('vrac_format', quantity);
      } else {
        url.searchParams.delete('vrac_format');
      }

      window.history.replaceState({ path: url.toString() }, '', url.toString());
    }
  }

  customElements.define('vrac-format-sync', VracFormatSync);
}
